// « Prendre la main » : traduction des evenements DOM du canevas en evenements du contrat
// POST inputUrl { events: [...] }. Les champs sont ceux de l'ancienne app Pirouette
// (openwork-server.ts, BrowserInputEvent, et runtime/apps/server/src/browser-input.ts), que le
// serveur traduit en commandes CDP (down -> mousePressed, up -> mouseReleased, move -> mouseMoved,
// key down/up -> keyDown/keyUp avec text).

export type BoutonCdp = "left" | "right" | "middle";

export type EvenementSaisie =
  | {
      kind: "mouse";
      type: "down" | "up" | "move";
      x: number;
      y: number;
      button?: BoutonCdp;
      clickCount?: number;
      buttons?: number;
      modifiers?: number;
    }
  | {
      kind: "wheel";
      x: number;
      y: number;
      deltaX: number;
      deltaY: number;
      modifiers?: number;
    }
  | {
      kind: "key";
      type: "down" | "up";
      key: string;
      code: string;
      text?: string;
      modifiers?: number;
    }
  | {
      kind: "navigate";
      action: "back" | "forward" | "reload" | "url";
      url?: string;
    };

export const LARGEUR_ECRAN_DEFAUT = 1280;
export const HAUTEUR_ECRAN_DEFAUT = 950;

const borner = (v: number, bas: number, haut: number) =>
  Math.max(bas, Math.min(haut, v));

/**
 * Point ecran (clientX/Y) vers le repere du navigateur distant (deviceWidth x deviceHeight), en
 * tenant compte des bandes d'un canevas en object-contain. Null si le point tombe dans une bande.
 */
export function versEcranDistant(args: {
  clientX: number;
  clientY: number;
  rect: { left: number; top: number; width: number; height: number };
  ecran: { width: number; height: number };
}): { x: number; y: number } | null {
  const { clientX, clientY, rect, ecran } = args;
  if (
    ecran.width <= 0 ||
    ecran.height <= 0 ||
    rect.width <= 0 ||
    rect.height <= 0
  ) {
    return null;
  }
  const echelle = Math.min(
    rect.width / ecran.width,
    rect.height / ecran.height
  );
  const largeurPeinte = ecran.width * echelle;
  const hauteurPeinte = ecran.height * echelle;
  const origineX = rect.left + (rect.width - largeurPeinte) / 2;
  const origineY = rect.top + (rect.height - hauteurPeinte) / 2;
  const fx = (clientX - origineX) / largeurPeinte;
  const fy = (clientY - origineY) / hauteurPeinte;
  if (fx < 0 || fx > 1 || fy < 0 || fy > 1) {
    return null;
  }
  return { x: fx * ecran.width, y: fy * ecran.height };
}

export function boutonCdp(bouton: number): BoutonCdp | undefined {
  if (bouton === 0) {
    return "left";
  }
  if (bouton === 1) {
    return "middle";
  }
  if (bouton === 2) {
    return "right";
  }
  return undefined;
}

/** Masque DOM `buttons` vers le masque CDP (gauche 1, droit 2, milieu 4). */
export function masqueBoutons(boutons: number): number {
  let masque = 0;
  if (boutons & 1) {
    masque |= 1;
  }
  if (boutons & 2) {
    masque |= 2;
  }
  if (boutons & 4) {
    masque |= 4;
  }
  return masque;
}

interface Touches {
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}

/** Codage CDP : Alt 1, Ctrl 2, Meta 4, Maj 8. */
export function modificateurs(e: Touches): number {
  return (
    (e.altKey ? 1 : 0) |
    (e.ctrlKey ? 2 : 0) |
    (e.metaKey ? 4 : 0) |
    (e.shiftKey ? 8 : 0)
  );
}

export function evenementSouris(
  type: "down" | "up" | "move",
  point: { x: number; y: number },
  e: { button: number; buttons: number; detail: number }
): EvenementSaisie {
  const x = Math.round(point.x);
  const y = Math.round(point.y);
  const buttons = masqueBoutons(e.buttons);
  if (type === "move") {
    return { kind: "mouse", type, x, y, buttons };
  }
  const button = boutonCdp(e.button);
  if (type === "up") {
    return {
      kind: "mouse",
      type,
      x,
      y,
      ...(button ? { button } : {}),
      buttons,
    };
  }
  return {
    kind: "mouse",
    type,
    x,
    y,
    ...(button ? { button } : {}),
    clickCount: borner(e.detail || 1, 1, 3),
    buttons,
  };
}

export function evenementMolette(
  point: { x: number; y: number },
  e: { deltaX: number; deltaY: number }
): EvenementSaisie {
  return {
    kind: "wheel",
    x: Math.round(point.x),
    y: Math.round(point.y),
    deltaX: borner(e.deltaX, -2000, 2000),
    deltaY: borner(e.deltaY, -2000, 2000),
  };
}

export function evenementClavier(
  type: "down" | "up",
  e: Touches & { key: string; code: string }
): EvenementSaisie {
  const imprimable = e.key.length === 1;
  return {
    kind: "key",
    type,
    key: e.key,
    code: e.code,
    ...(type === "down" && imprimable ? { text: e.key } : {}),
    modifiers: modificateurs(e),
  };
}

/** Taille maximale d'un lot (le serveur de l'ancienne app refusait au-dela de 64). */
export const TAILLE_LOT = 64;
